/**
 * "Add a Figma prototype" modal, opened from the site-creation panel and from
 * the Settings "Site source" row.
 *
 * Takes the prototype link straight away (one input, Embed by default), checks
 * it, saves it on every default viewport and counts figma_embed. Per-screen-size
 * links can be changed later in Settings. The site serves through the folio
 * renderer at aeroapp.ai/{slug}.
 *   - Other ways > Convert: hand off to the AI-agent prompt (AgentPublishModal), which turns
 *     the design into real code and publishes it as an html site.
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import firebase from '../../firebase';
import AgentPublishModal from '../../components/shared/AgentPublishModal';
import FigmaShareHint from '../../components/Aero/FigmaShareHint';
import { checkFigmaLink } from '../../utils/figmaLinkCheck';
import { convertPastedLink } from '../../utils/figmaPaste';
import AeroFigmaLinkField from '../../components/Aero/AeroFigmaLinkField';
import { editFigmaUrl } from '../../utils/figmaEmbedUrl';
import { getDefaultViewports, viewportsToLegacyFormat } from '../../utils/viewportUtils';
import { recordCreationSource } from '../../utils/creationSource';
import { startTrialClock, isLiveSiteDoc } from '../../utils/trialClock';

function FigmaLinkModal({ user, ownerUid, site, onClose, onEmbedded, onSwitchToAgent }) {
  // Shared sites write to the OWNER's path, not the logged-in admin's.
  const writeOwnerUid = ownerUid || user.uid;
  const navigate = useNavigate();
  const [link, setLink] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAgent, setShowAgent] = useState(false);

  // Embed: validate the pasted link, save it onto every default viewport and
  // go straight to the site. Merge, so any html data on the doc is untouched.
  const handleEmbed = async () => {
    if (saving) return;
    const check = checkFigmaLink(link);
    if (!check.ok) {
      setError(check.message);
      return;
    }
    // A design or file link is turned into a prototype link first (the field already does this on
    // paste; this covers a link that was typed and saved without leaving the field).
    const prototypeLink = convertPastedLink(link).link;
    setSaving(true);
    setError(null);
    try {
      const viewports = getDefaultViewports().map((v) => ({ ...v, figmaUrl: editFigmaUrl(prototypeLink) }));
      await firebase.firestore()
        .collection('user').doc(writeOwnerUid)
        .collection('url').doc(site.id)
        .set(
          {
            userId: writeOwnerUid,
            type: 'figma',
            aeroSite: true,
            isDraft: 'false',
            draft: firebase.firestore.FieldValue.delete(),
            creationSource: 'figma_embed',
            viewports,
            urls: viewportsToLegacyFormat(viewports),
            updatedAt: new Date(),
          },
          { merge: true }
        );
      recordCreationSource('figma_embed', 'aero');
      // The first site to go live starts the free plan's 7 day clock (owners
      // only; a collaborator cannot write the owner's user doc).
      await startTrialClock(firebase.firestore(), writeOwnerUid, site.id, { wasLive: isLiveSiteDoc(site) });
      if (onEmbedded) await onEmbedded();
      onClose();
      navigate(`/sites/${site.id}`);
    } catch (err) {
      console.error('Error setting up Figma prototype:', err);
      setError('Could not set up the prototype: ' + err.message);
      setSaving(false);
    }
  };

  // Convert: defer to the caller's agent handoff when given, otherwise own the
  // AgentPublishModal so the convert path works from every entry point.
  const handleConvert = () => {
    if (onSwitchToAgent) {
      onClose();
      onSwitchToAgent();
    } else {
      setShowAgent(true);
    }
  };

  if (showAgent) {
    return (
      <AgentPublishModal
        user={user}
        site={site}
        onClose={() => {
          setShowAgent(false);
          onClose();
        }}
      />
    );
  }

  return ReactDOM.createPortal(
    <div className="ut-modal-root aero-modal-backdrop ut-theme" onClick={() => !saving && onClose()}>
      <div className="aero-modal" onClick={(e) => e.stopPropagation()}>
        <div className="aero-modal-head">
          <h2>Add a Figma prototype</h2>
          <button
            type="button"
            className="aero-btn aero-btn-ghost aero-btn-icon"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="aero-modal-body">
          <div className="ut-create-body">
            <div>
              <div className="ut-create-section-label">Figma prototype link</div>
              <AeroFigmaLinkField
                value={link}
                onChange={(next) => { setLink(next); setError(null); }}
                disabled={saving}
                autoFocus
              />
              <FigmaShareHint withPassword />
              {error && <div className="ut-create-hint error">{error}</div>}
            </div>
            <details className="ut-create-other-ways">
              <summary>Other ways</summary>
              <button type="button" className="ut-create-source-card" onClick={handleConvert} disabled={saving}>
                <span className="ut-create-source-title">Convert with AI</span>
                <span className="ut-create-source-desc">
                  Your AI agent rebuilds the design as real code and publishes it here. Needs a paid Figma seat.
                </span>
              </button>
            </details>
          </div>
        </div>

        <div className="aero-modal-foot">
          <button
            type="button"
            className="aero-btn aero-btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="aero-btn aero-btn-primary"
            onClick={handleEmbed}
            disabled={saving || !checkFigmaLink(link).ok}
          >
            {saving ? 'Saving…' : 'Embed prototype'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default FigmaLinkModal;
